import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence, signOut } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { firebaseConfig } from '../firebase/config';

// Declare global window extensions for dev helpers
declare global {
  interface Window {
    __BF?: {
      auth?: any;
      db?: any;
    };
  }
}

// Initialize the modern (v9) app once. This is the single source of truth.
const app = initializeApp(firebaseConfig);

// Get and export all the v9 services.
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, firebaseConfig.functionsRegion);
export const storage = getStorage(app);

// Dev helper: always enable in our dev environment
if (typeof window !== 'undefined' && process.env.REACT_APP_ENV === 'dev') {
  try {
    if (!window.__BF) {
      window.__BF = { auth: undefined, db: undefined };
    }
    window.__BF.auth = auth;
    window.__BF.db = db;
    console.info('[Valutten] Dev helpers ready on window.__BF');
  } catch (e: any) {
  // console.error('[Valutten] Failed to init dev helpers', e);
  }
}

// Initialize App Check using the single app instance.
// TEMPORARILY DISABLED (2025-11-17) - AppCheck causing 403 errors in DEV/STAGE
// Re-enable after registering fresh debug tokens in Firebase Console
if (typeof window !== 'undefined') {
  const siteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
  const env = process.env.REACT_APP_ENV || 'dev';

  // Only enable AppCheck in production
  if (siteKey && env === 'prod') {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      console.log('[AppCheck] Initialized successfully for PROD');
    } catch (e: any) {
      console.error('[AppCheck] Initialization failed:', e);
    }
  } else {
    console.log(`[AppCheck] Disabled for ${env} environment (throttling prevention)`);
  }
}

// Set persistence — browserSessionPersistence clears on browser close (enterprise security).
setPersistence(auth, browserSessionPersistence).catch(() => {});

// One-time migration: clear legacy localStorage auth tokens left by browserLocalPersistence.
// Without this, Firebase restores the old localStorage token on init, bypassing session persistence.
// Runs once per browser, then the flag prevents re-running.
const SESSION_MIGRATION_KEY = 'valutten-session-v1';
if (typeof window !== 'undefined' && !localStorage.getItem(SESSION_MIGRATION_KEY)) {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('firebase:authUser:')) {
      localStorage.removeItem(key);
    }
  });
  localStorage.setItem(SESSION_MIGRATION_KEY, 'true');
  // Force sign-out so onAuthStateChanged doesn't fire with the stale user
  signOut(auth).catch(() => {});
}
